import * as React from 'react';
import { IRow } from "../common/timeline/TimelineList";
import * as TimelineList from '../common/timeline/TimelineList';
import * as utils from "../common/timeline/utils";
import { ITask, PredecessorType } from '../../entities/Subentities';
import { EntityGroup } from "../common/extensibleEntity/EntityGroupHeader";
import { Dictionary, IExtensibleEntity, MaybeDate } from "../../entities/common";
import TaskIcon from './TaskIcon';
import { PersonaSize } from 'office-ui-fabric-react';
import { adjustProgress, HUNDRED_PCT, toDate } from '../utils/common';
import ImagePersona from '../common/ImagePersona';

import TaskTooltipContent from './TaskTooltipContent';
import TaskGroupTooltipContent, { GroupSummary } from './TaskGroupTooltipContent';
import { ITimelineMarker } from '../common/timeline/TimelineMarker';
import { Visibility, ITimelineSegment } from '../common/timeline/TimelineSegment';
import { ITimelineRelation, TimelineEntityType, TimelineRelationType } from '../common/timeline/TimelineRelation';
import { ungroupedGroup } from '../common/inputs/GroupDropdown';
import { ITimelineBaselineSegment } from '../common/timeline/TimelineBaselineSegment';
import { calculateShadeColor } from '../common/ShadableEntityIcon';
import { ITimelineActualDatesSegment } from '../common/timeline/TimelineActualDatesSegment';
import { CalendarDataSet } from '../../store/calendar/common';

const taskColorLightnessPercent: number = .2;
const taskShadeColorLightnessPercent: number = .5;

function buildSegments(key: string, minDate: Date, maxDate: Date, color?: string, lightnessPercent?: number): ITimelineSegment[] {
    if (!color) {
        color = ungroupedGroup.color;
    }
    const shadeColor = calculateShadeColor(color, lightnessPercent ?? taskShadeColorLightnessPercent);

    return [{
        key: key,
        startDate: minDate.getBeginOfDay(),
        finishDate: maxDate.getEndOfDay(),
        datesVisibility: Visibility.OnHover,
        className: `start-to-finish`,
        style: { backgroundColor: shadeColor, borderColor: color }
    }];
}

export type TaskRelation = { relatedId: string, type: PredecessorType, isReverse?: boolean, isExternal?: boolean };
export function buildTimelineItem(task: ITask, tasksMap: Dictionary<ITask>, children?: TaskRelation[], showCriticalPath?: boolean): IRow {
    const startDate = toDate(task.attributes.StartDate),
        finishDate = toDate(task.attributes.DueDate);
    const isMilestone = task.attributes.IsMilestone;
    const isCritical = showCriticalPath && !!task.attributes.Critical;

    let segments: ITimelineSegment[] = [];
    const markers: ITimelineMarker[] = [];
    let relations: ITimelineRelation[] = [];
    if (startDate || finishDate) {
        const taskColor = getTaskColor(task);
        if (isMilestone) {
            if (finishDate) {
                markers.push({
                    key: task.id,
                    date: finishDate.getBeginOfDay(),
                    label: task.attributes.Name,
                    datesVisibility: Visibility.OnHover,
                    style: { backgroundColor: taskColor }
                });
            }
        }
        else {
            segments = [...segments, ...buildSegments(task.id, startDate ?? finishDate!, finishDate ?? startDate!, taskColor)];
        }

        if (children?.length) {
            relations = children.reduce<ITimelineRelation[]>((acc, relation) => {
                const parent = markers[0]
                    ? { ...markers[0], type: TimelineEntityType.Marker, key: task.id }
                    : segments[0]
                        ? { ...segments[0], type: TimelineEntityType.Segment, key: task.id }
                        : undefined;

                if (!parent) {
                    return acc;
                }

                if (relation.isExternal) {
                    const child = markers[0]
                        ? { ...markers[0], type: TimelineEntityType.Marker, key: relation.relatedId }
                        : segments[0]
                            ? { ...segments[0], type: TimelineEntityType.Segment, key: relation.relatedId }
                            : undefined;

                    if (!child) {
                        return acc;
                    }

                    const externalRelatedTask = tasksMap[relation.relatedId];
                    const isCriticalExternalRelation = isCritical && !!externalRelatedTask?.attributes.Critical;
                    acc.push({
                        key: `${task.id}_${relation.relatedId}`,
                        type: toRelationType(relation.type),
                        parent,
                        child,
                        isReverse: relation.isReverse,
                        isExternal: true,
                        className: isCriticalExternalRelation ? 'critical' : undefined
                    } as ITimelineRelation);
                    return acc;
                }

                const relatedTask = tasksMap[relation.relatedId];
                if (!relatedTask) {
                    return acc;
                }

                const childRow = buildTimelineItem(relatedTask, tasksMap, undefined, showCriticalPath);
                if (!childRow.markers[0] && !childRow.segments[0]) {
                    return acc;
                }

                const isCriticalRelation = isCritical && !!relatedTask.attributes.Critical;
                acc.push({
                    key: `${task.id}_${childRow.key}`,
                    type: toRelationType(relation.type),
                    parent,
                    child: childRow.markers[0]
                        ? { ...childRow.markers[0], type: TimelineEntityType.Marker, key: childRow.key }
                        : childRow.segments[0]
                            ? { ...childRow.segments[0], type: TimelineEntityType.Segment, key: childRow.key }
                            : undefined,
                    isReverse: relation.isReverse,
                    className: isCriticalRelation ? 'critical' : undefined
                } as ITimelineRelation);

                return acc;
            }, []).filter(_ => _.parent && _.child);
        }
    }
    if (segments.length) {
        segments[segments.length - 1].addContent = true;
    }

    if (isCritical) {
        segments = segments.map(_ => ({ ..._, className: `${_.className ?? ''} critical`.trim() }));
    }

    const baselines = buildBaselines(task);
    const actualDates = buildActualDates(task);

    return {
        key: task.id,
        entity: task,
        markers,
        segments,
        relations,
        baselineSegments: baselines,
        actualDatesSegments: actualDates
    };
}

function buildBaselines(task: ITask): ITimelineBaselineSegment[] {
    const baselineStartDate = toDate(task.baseline?.startDate);
    const baselineDueDate = toDate(task.baseline?.dueDate);
    if (task.attributes.IsMilestone) {
        return baselineDueDate 
            ? [{
                key: task.id,
                finishDate: baselineDueDate.getBeginOfDay(),
                isMilestone: true
            }]
            : [];
    }  
    if (baselineStartDate || baselineDueDate) {
        return [{
            key: task.id,
            startDate: (baselineStartDate || baselineDueDate!).getBeginOfDay(),
            finishDate: (baselineDueDate || baselineStartDate!).getEndOfDay(),
        }];
    }
    return [];
}

function buildActualDates(task: ITask): ITimelineActualDatesSegment[] {
    const startDate = toDate(task.attributes?.ActualStartDate);
    const dueDate = toDate(task.attributes?.ActualDueDate);
    
    if(!startDate && !dueDate) {
        return [];
    }

    return [{
        key: task.id,
        startDate: (startDate || dueDate!).getBeginOfDay(),
        finishDate: (dueDate || startDate!).getEndOfDay(),
    }];
}

function toRelationType(predecessorType: PredecessorType): TimelineRelationType {
    switch (predecessorType) {
        case PredecessorType.FinishToStart: return TimelineRelationType.RightToLeft
        case PredecessorType.FinishToFinish: return TimelineRelationType.RightToRight
        case PredecessorType.StartToFinish: return TimelineRelationType.LeftToRight
        case PredecessorType.StartToStart: return TimelineRelationType.LeftToLeft
        default: return TimelineRelationType.RightToLeft;
    }
}

function getTaskColor(task: ITask): string {
    const color = task.attributes.Group?.color || ungroupedGroup.color;
    return calculateShadeColor(color, taskColorLightnessPercent)!;
}

const leafTasksFilter = (_: ITask): boolean => !_.hierarchy.isParent;

export function calculateTaskGroupSummaryUsingAverageProgress(tasks: ITask[], calendar: CalendarDataSet, tasksFilter?: (task: ITask) => boolean): GroupSummary {
    const leafTasks = tasks.filter(_ => tasksFilter?.(_) ?? leafTasksFilter(_));
    const leafsSum = leafTasks.reduce<{ effort: number, progress: number, storyPoints: number }>((prev, task, index, arr) => {
        return {
            effort: prev.effort + (task.attributes.Effort || 0),
            progress: prev.progress + (task.attributes.Progress || 0),
            storyPoints: prev.storyPoints + (task.attributes.StoryPoints || 0),
        }
    }, { effort: 0, progress: 0, storyPoints: 0 });

    const tasksCountAndDates = calculateTasksCountAndDates(tasks, calendar);
    const progress = !leafTasks.length ? 0 : leafsSum.progress / leafTasks.length;

    return {
        ...tasksCountAndDates,
        progress: adjustProgress(progress),
        effort: leafTasks.every(_ => _.attributes.Effort === undefined) ? "N/A" : leafsSum.effort,
        storyPoints: leafTasks.every(_ => _.attributes.StoryPoints === undefined) ? "N/A" : leafsSum.storyPoints
    }
}

export function calculateTaskGroupSummaryUsingEffort(tasks: ITask[], calendar: CalendarDataSet): GroupSummary {
    const leafsSum = tasks.filter(leafTasksFilter).reduce<{ effort: number, storyPoints: number, completedWork: number }>((prev, task, index, arr) => {
        return {
            effort: prev.effort + (task.attributes.Effort || 0),
            storyPoints: prev.storyPoints + (task.attributes.StoryPoints ?? 0),
            completedWork: prev.completedWork + (task.attributes.CompletedWork || 0)
        }
    }, { effort: 0, completedWork: 0, storyPoints: 0 });

    const progress = !leafsSum.effort ? 0 : (HUNDRED_PCT * leafsSum.completedWork / leafsSum.effort);
    const tasksCountAndDates = calculateTasksCountAndDates(tasks, calendar);

    return {
        ...tasksCountAndDates,
        progress: adjustProgress(progress),
        effort: leafsSum.effort,
        storyPoints: leafsSum.storyPoints
    }
}

export function calculateTaskGroupSummaryUsingStoryPoints(tasks: ITask[], calendar: CalendarDataSet): GroupSummary {
    const leafsSum = tasks.filter(leafTasksFilter)
        .reduce<{ effort: number, storyPoints: number, completedStoryPoints: number }>(
            (prev, task) => ({
                effort: prev.effort + (task.attributes.Effort ?? 0),
                storyPoints: prev.storyPoints + (task.attributes.StoryPoints ?? 0),
                completedStoryPoints: prev.completedStoryPoints + (task.attributes.CompletedStoryPoints ?? 0)
            }),
            { effort: 0, storyPoints: 0, completedStoryPoints: 0 });

    const progress = !leafsSum.storyPoints ? 0 : (HUNDRED_PCT * leafsSum.completedStoryPoints / leafsSum.storyPoints);
    const tasksCountAndDates = calculateTasksCountAndDates(tasks, calendar);

    return {
        ...tasksCountAndDates,
        progress: adjustProgress(progress),
        effort: leafsSum.effort,
        storyPoints: leafsSum.storyPoints
    }
}

function calculateTasksCountAndDates(tasks: ITask[], calendar: CalendarDataSet): { startDate: Date | undefined, dueDate: Date | undefined, duration: number,
    tasksCounts: { new: number, inProgress: number, complete: number, total: number } } {
        
    const sum = tasks.reduce<{ new: number, inProgress: number, complete: number }>((prev, task, index, arr) => {
        return {
            inProgress: task.attributes.Progress !== 0 && task.attributes.Progress !== HUNDRED_PCT ? (prev.inProgress + 1) : prev.inProgress,
            new: task.attributes.Progress === 0 ? (prev.new + 1) : prev.new,
            complete: task.attributes.Progress === HUNDRED_PCT ? (prev.complete + 1) : prev.complete,
        }
    }, { new: 0, inProgress: 0, complete: 0 });

    const dates: MaybeDate[] = [...tasks.map(_ => ([_.attributes.StartDate, _.attributes.DueDate])).reduce((a, b) => a.concat(b), [])];
    const { minDate, maxDate } = utils.minMax(dates);
    const duration = minDate && maxDate && utils.getWorkingDaysBetweenDates(minDate.getBeginOfDay(), maxDate.getBeginOfDay(), calendar);

    return {
        startDate: minDate?.getBeginOfDay(),
        dueDate: maxDate?.getBeginOfDay(),
        duration: duration ?? 0,
        tasksCounts: {
            new: sum.new,
            inProgress: sum.inProgress,
            complete: sum.complete,
            total: tasks.length
        }
    }
}

export function estimateTaskEffort(task: ITask, calendar: CalendarDataSet): number | undefined {
    const { minDate, maxDate } = utils.minMax([task.attributes.StartDate, task.attributes.DueDate]);
    if (minDate && maxDate) {
        return utils.getWorkingHoursBetweenDates(minDate, maxDate, calendar);
    }
}

type GroupRowEntityAttrs = { group: EntityGroup, summary: GroupSummary }
type GroupRowEntity = IExtensibleEntity & { attributes: GroupRowEntityAttrs; }

const groupRowType = "group";

export function getGroupRow(group: EntityGroup): TimelineList.IRow {
    let segments: ITimelineSegment[] = [];
    const summary: GroupSummary = group.data as GroupSummary;

    if (summary.startDate && summary.dueDate && summary.startDate !== summary.dueDate) {
        segments = buildSegments(group.key, summary.startDate, summary.dueDate, group.color);
    }

    const entity: GroupRowEntity = {
        id: '', attributes: {
            group,
            summary
        }
    };
    return {
        key: group.key,
        rowType: groupRowType,
        entity,
        segments,
        markers: []
    }
}
export function renderSegmentTooltipContent(_: TimelineList.IRow, segment: ITimelineSegment, openTask: (task: ITask) => void, projectId: string): JSX.Element | undefined {
    return _.rowType === groupRowType
        ? <TaskGroupTooltipContent
            group={{ name: (_.entity as GroupRowEntity).attributes.group.name, color: (_.entity as GroupRowEntity).attributes.group.color }}
            summary={(_.entity as GroupRowEntity).attributes.summary}
        />
        : <TaskTooltipContent 
            task={_.entity as ITask}
            onClick={() => openTask(_.entity as ITask)} />;
}
export function renderSegmentContent(_: TimelineList.IRow, segment: ITimelineSegment): JSX.Element | undefined {
    if (_.rowType === groupRowType) {
        const groupEntity: GroupRowEntity = _.entity as GroupRowEntity;
        const { total, complete } = groupEntity.attributes.summary.tasksCounts;
        return <TimelineProgress progress={groupEntity.attributes.summary.progress} color={groupEntity.attributes.group.color ?? ungroupedGroup.color}>
            <div className="subitems-counter">{complete} / {total} <span className="extra">task{total != 1 ? 's' : ''}</span></div>
        </TimelineProgress>;
    }

    const task = _.entity as ITask;
    const assignee = task.attributes.AssignedTo?.[0] || undefined;

    return <TimelineProgress progress={task.attributes.Progress} color={getTaskColor(task)}>
        <div className="timeline-task-icon-wrapper">
            {
                assignee
                    ? <ImagePersona size={PersonaSize.size16} info={assignee} text={assignee.fullName} hidePersonaDetails />
                    : <TaskIcon entity={task} />
            }
        </div>
    </TimelineProgress>;
}

export const TimelineProgress: React.FunctionComponent<{ progress: number | undefined, color: string | undefined }> = props => {
    const { progress, color, children } = props;
    return <div
        className={`progress ${progress === 0 ? 'empty' : ''}`}
        style={{
            width: `${Math.max(Math.min(progress || 0, 100), 0)}%`,
            backgroundColor: color,
            borderColor: color
        }}>
        {children}
    </div>
}